import { faClose } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ReactNode, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

/**
 * A controlled modal for a single, committing action.
 *
 * The listing form used to render at the bottom of the page, below the whole
 * grid. Selecting an NFT near the top of 262 items put the price field several
 * screens away with no indication it had appeared -- the form was "open" and
 * invisible. A dialog puts it where the eye already is, whatever the scroll
 * position, and names the item so there is no doubt what is being listed.
 *
 * `features/store`'s dialog is the closest existing thing but is trigger-based
 * and bound to its own context; this one is driven by `open`, because the
 * selection lives in the panel's state. Its layout lesson is copied verbatim
 * and is the important part -- see the overlay comment below.
 *
 * ## Behaviours that are not optional here
 *
 * This dialog can be open while a wallet signature is in flight, so `busy`
 * disables every route out of it. Closing mid-signature would leave the user
 * with a wallet prompt for a listing the page has forgotten about.
 */
export const TradeDialog = ({
    open,
    title,
    busy = false,
    onClose,
    children,
}: {
    open: boolean
    title: string
    /** While true, Escape, the overlay and the close button are all inert. */
    busy?: boolean
    onClose: () => void
    children: ReactNode
}) => {
    const panelRef = useRef<HTMLDivElement>(null)
    const returnFocusTo = useRef<HTMLElement | null>(null)

    // Remember what opened it, so focus goes back there on close rather than
    // to the top of the document -- which on this page means losing your place
    // in a grid of several hundred cards.
    useEffect(() => {
        if (!open) return
        returnFocusTo.current = document.activeElement as HTMLElement

        const firstField = panelRef.current?.querySelector<HTMLElement>(
            'input, select, textarea, button'
        )
        firstField?.focus()

        return () => returnFocusTo.current?.focus?.()
    }, [open])

    // The page behind must not scroll while this is open: on a phone, dragging
    // the dialog scrolls the grid underneath and the panel appears to drift.
    useEffect(() => {
        if (!open) return
        const previous = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            document.body.style.overflow = previous
        }
    }, [open])

    useEffect(() => {
        if (!open) return

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && !busy) {
                onClose()
                return
            }
            if (event.key !== 'Tab') return

            // Focus trap. Without it, Tab walks out of the dialog and into the
            // grid behind, where the focus ring is invisible under the overlay.
            const focusable = panelRef.current?.querySelectorAll<HTMLElement>(
                'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
            )
            if (!focusable?.length) return

            const first = focusable[0]
            const last = focusable[focusable.length - 1]
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault()
                last.focus()
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault()
                first.focus()
            }
        }

        document.addEventListener('keydown', onKeyDown)
        return () => document.removeEventListener('keydown', onKeyDown)
    }, [open, busy, onClose])

    if (!open) return null

    return createPortal(
        // The overlay owns the viewport and the panel is capped to it, scrolling
        // its own overflow. Without the cap a panel taller than the screen
        // overflows a centred flex line in BOTH directions, putting the header
        // and the close button above the top of the screen with no way to
        // scroll them back -- the bug `fix/store-checkout-modal-mobile` fixed,
        // and this form is long enough on a phone to reproduce it.
        <div
            className='fixed inset-0 z-[1000] flex items-end justify-center bg-black/80 p-0 sm:items-center sm:p-4'
            onMouseDown={(e) => {
                if (e.target === e.currentTarget && !busy) onClose()
            }}
        >
            <div
                ref={panelRef}
                role='dialog'
                aria-modal='true'
                aria-label={title}
                // Bottom sheet on phones, centred card from sm up. A centred
                // box on a small screen puts the confirm button under the
                // thumb's blind spot; anchored to the bottom it is where the
                // hand already is.
                className='max-h-full w-full max-w-lg overflow-y-auto overscroll-contain border border-white/20 bg-black sm:rounded'
            >
                <div className='sticky top-0 flex flex-row items-center justify-between border-b border-white/10 bg-black px-4 py-3'>
                    <p className='truncate text-sm font-bold text-white'>{title}</p>
                    <button
                        onClick={onClose}
                        disabled={busy}
                        aria-label='Close'
                        className='px-2 text-white/50 hover:text-white disabled:opacity-30'
                    >
                        <FontAwesomeIcon icon={faClose} />
                    </button>
                </div>
                <div className='p-4'>{children}</div>
            </div>
        </div>,
        document.body
    )
}
